/**
 * A folha do documento da mesa (E202.F1, ADR-0145): as seções como o papel as imprime, com as notas
 * novas e o "Alterar na mesa" de cada seção. E205.F2 (ADR-0147 §2): com a seção sozinha na aba Exame, a
 * âncora fica no bloco que leva a navegação junto (`ancorada` falso).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import type { SecaoDoDocumentoData } from '@/lib/documentoDaSaida';

import { BlocoDoDocumento, type ContextoDasPartes } from './BlocoDoDocumento';
import { NotasNovas } from './NotasNovas';
import { PALETA } from './paleta';

const t = ptBR.documentoDaMesa;

function Secao({
  secao,
  contexto,
  ancorada = true,
}: {
  secao: SecaoDoDocumentoData;
  contexto: ContextoDasPartes;
  /** Com a seção sozinha, a âncora fica no bloco que leva a navegação junto. */
  ancorada?: boolean;
}): JSX.Element {
  return (
    <section
      id={ancorada ? secao.ancora : undefined}
      className="scroll-mt-sc-6"
      data-testid="documento-secao"
      data-ancora={secao.ancora}
    >
      {secao.blocos.map((bloco, i) => (
        <BlocoDoDocumento key={i} bloco={bloco} contexto={contexto} secao={secao.chave} />
      ))}
      <NotasNovas secao={secao} contexto={contexto} />
      {secao.atalho !== null ? (
        <p className="mt-1 text-right text-[0.85em]">
          <Link
            to={{ hash: secao.atalho }}
            className="font-medium underline underline-offset-2"
            style={{ color: PALETA.navy2 }}
            data-testid={`documento-alterar-${secao.atalho}`}
          >
            {t.alterarNaMesa}
          </Link>
        </p>
      ) : null}
    </section>
  );
}

/** E205.F2: a seção à vista e a navegação que a cerca, no alto e no fim. */
export interface PorSecao {
  ativa: string | null;
  alto: ReactNode;
  fim: ReactNode;
}

export function Folha({
  secoes,
  contexto,
  sozinha,
}: {
  secoes: readonly SecaoDoDocumentoData[];
  contexto: ContextoDasPartes;
  sozinha: boolean;
}): JSX.Element {
  return (
    <article
      aria-label={t.rotulo}
      className="mx-auto max-w-[56rem] border border-border px-sc-6 py-sc-6 text-[13px] leading-relaxed"
      style={{ background: '#FFFFFF', color: PALETA.tinta, containerType: 'inline-size' }}
      data-testid="documento-papel"
    >
      {secoes.map((secao) => (
        <Secao key={secao.ancora} secao={secao} contexto={contexto} ancorada={!sozinha} />
      ))}
    </article>
  );
}
